import type { ConsentItem } from './types';

/**
 * Supply-side (caregiver) consent items for ch-dsg-health.
 *
 * 4 granular, unbundled checkboxes per FADP (Swiss Federal Act on Data Protection).
 * All required: fail-closed, no outreach without all four checked.
 * All text in German using ae/oe/ue convention (no umlauts).
 */
const CH_DSG_HEALTH_SUPPLY: ConsentItem[] = [
  {
    id: 'personal_data_processing',
    purpose: 'personal_data_processing',
    text: 'Ich willige in die Verarbeitung meiner personenbezogenen Daten zum Zweck der Stellenvermittlung ein. (Erforderlich)',
    required: true,
  },
  {
    id: 'qualification_sharing',
    purpose: 'qualification_sharing',
    text: 'Ich willige ein, dass meine Qualifikationen und Erfahrungen an potenzielle Arbeitgeber weitergegeben werden. (Erforderlich)',
    required: true,
  },
  {
    id: 'outreach_contact',
    purpose: 'outreach_contact',
    text: 'Ich willige ein, per E-Mail, SMS oder Telefon ueber passende Stellenangebote kontaktiert zu werden. (Erforderlich)',
    required: true,
    channel: 'email',
  },
  {
    id: 'privacy_policy_acknowledgement',
    purpose: 'privacy_policy_acknowledgement',
    text: 'Ich nehme die Datenschutzerklaerung zur Kenntnis und bin ueber mein Recht informiert, meine Einwilligung jederzeit zu widerrufen. (Erforderlich)',
    required: true,
  },
];

/**
 * Demand-side (patient/family) consent items for ch-dsg-health.
 *
 * 4 granular, unbundled checkboxes per FADP (Swiss Federal Act on Data Protection).
 * Health data requires explicit consent under FADP Art. 6(6)-(7).
 * All required: fail-closed, no outreach without all four checked.
 * All text in German using ae/oe/ue convention (no umlauts).
 */
const CH_DSG_HEALTH_DEMAND: ConsentItem[] = [
  {
    id: 'health_data_processing',
    purpose: 'health_data_processing',
    text: 'Ich willige in die Verarbeitung meiner gesundheitsbezogenen Daten zum Zweck der Pflegevermittlung ein. (Erforderlich)',
    required: true,
  },
  {
    id: 'care_requirements_sharing',
    purpose: 'care_requirements_sharing',
    text: 'Ich willige ein, dass meine Pflegeanforderungen an qualifizierte Pflegefachpersonen weitergegeben werden. (Erforderlich)',
    required: true,
  },
  {
    id: 'outreach_contact',
    purpose: 'outreach_contact',
    text: 'Ich willige ein, ueber passende Pflegeangebote kontaktiert zu werden. (Erforderlich)',
    required: true,
    channel: 'email',
  },
  {
    id: 'privacy_policy_acknowledgement',
    purpose: 'privacy_policy_acknowledgement',
    text: 'Ich nehme die Datenschutzerklaerung zur Kenntnis und bin ueber mein Recht informiert, meine Einwilligung jederzeit zu widerrufen. (Erforderlich)',
    required: true,
  },
];

/**
 * Default single consent item for jurisdictions that do not require granular consent.
 * Matches the existing single-checkbox behavior for backwards compatibility.
 */
function createDefaultConsentItem(jurisdiction: string): ConsentItem {
  switch (jurisdiction) {
    case 'us-tcpa':
      return {
        id: 'general_consent',
        purpose: 'general_consent',
        text: 'By checking this box, I provide my express written consent to be contacted by phone calls, text messages (SMS/MMS), and email at the number and email address I have provided, including through the use of automated dialing technology and prerecorded or artificial voice messages. Message and data rates may apply. I understand that my consent is not a condition of any purchase and I may revoke my consent at any time.',
        required: true,
        channel: 'email',
      };

    case 'eu-gdpr':
      return {
        id: 'general_consent',
        purpose: 'general_consent',
        text: 'I agree to the processing of my personal data as described above for the purpose of receiving communications. I understand that I may withdraw my consent at any time. For more information, please see our Privacy Policy.',
        required: true,
        channel: 'email',
      };

    case 'za-popia':
      return {
        id: 'general_consent',
        purpose: 'general_consent',
        text: 'By checking this box, I consent to the collection and processing of my personal information in accordance with the Protection of Personal Information Act (POPIA). I understand that I may withdraw my consent at any time by contacting us.',
        required: true,
        channel: 'email',
      };

    default:
      return {
        id: 'general_consent',
        purpose: 'general_consent',
        text: 'I consent to the processing of my personal data as described above. I understand that I may withdraw my consent at any time.',
        required: true,
        channel: 'email',
      };
  }
}

/**
 * Get the consent items for a given jurisdiction and form side.
 *
 * For ch-dsg-health, returns 4 granular checkboxes (supply or demand).
 * For ch-dsg, returns 4 granular checkboxes matching the supply-side pattern.
 * For all other jurisdictions, returns a single item matching current behavior
 * (backwards compatible).
 *
 * @param jurisdiction - The compliance jurisdiction (e.g. "ch-dsg-health", "us-tcpa")
 * @param side - Whether this is a supply-side or demand-side form
 * @returns Array of ConsentItem objects for rendering checkboxes
 */
export function getConsentItems(
  jurisdiction: string,
  side: 'supply' | 'demand',
): ConsentItem[] {
  if (jurisdiction === 'ch-dsg-health') {
    return side === 'supply' ? CH_DSG_HEALTH_SUPPLY : CH_DSG_HEALTH_DEMAND;
  }

  if (jurisdiction === 'ch-dsg') {
    // ch-dsg: generic Swiss consent (no health data, no Spitex-specific language)
    return [
      {
        id: 'personal_data_processing',
        purpose: 'personal_data_processing',
        text: 'Ich willige in die Verarbeitung meiner personenbezogenen Daten zum angegebenen Zweck ein. (Erforderlich)',
        required: true,
      },
      {
        id: 'email_contact',
        purpose: 'email_contact',
        text: 'Ich willige ein, per E-Mail kontaktiert zu werden. (Erforderlich fuer E-Mail-Kontakt)',
        required: true,
        channel: 'email',
      },
      {
        id: 'whatsapp_contact',
        purpose: 'whatsapp_contact',
        text: 'Ich willige ein, per WhatsApp kontaktiert zu werden. (Optional)',
        required: false,
        channel: 'whatsapp',
      },
      {
        id: 'data_sharing',
        purpose: 'data_sharing',
        text: 'Ich willige ein, dass meine Daten an Dritte zum angegebenen Zweck weitergegeben werden. (Erforderlich fuer Vermittlung)',
        required: true,
      },
    ];
  }

  // All other jurisdictions: single item for backwards compatibility
  return [createDefaultConsentItem(jurisdiction)];
}
